import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { financeApi } from '../../services/financeApi.js';
import { hrApi } from '../../services/hrApi.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { projectsApi } from '../../services/projectsApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency } from '../../utils/formatters.js';

const PAGE_CAP = 8;
const GROUP_CAP = 5;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const LIST_FETCH_LIMIT = 200;

// Module-level opener so the topbar trigger (or anything else) can open the
// palette without prop drilling - same pattern as Toaster's showToast().
let openHandler = null;
export function openCommandPalette() {
  openHandler?.();
}

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function matches(haystack, needle) {
  return (haystack || '').toLowerCase().includes(needle);
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </svg>
  );
}

function PaletteSkeleton() {
  return (
    <div className="palette-skeleton" aria-hidden="true">
      <span className="skeleton-line" />
      <span className="skeleton-line" />
      <span className="skeleton-line" />
    </div>
  );
}

function PaletteGroup({ group, selectedIndex, onHover, onActivate }) {
  return (
    <div className="palette-group" role="group" aria-label={group.label}>
      <div className="palette-group-title">{group.label}</div>
      {group.items.map((item) => (
        <div
          key={`${item.kind}-${item.id}`}
          id={`palette-opt-${item.flatIndex}`}
          role="option"
          aria-selected={item.flatIndex === selectedIndex}
          className={`palette-row${item.flatIndex === selectedIndex ? ' selected' : ''}`}
          onMouseMove={() => onHover(item.flatIndex)}
          onClick={() => onActivate(item)}
        >
          <span className="palette-row-label">{item.label}</span>
          {item.secondary && <span className="palette-row-secondary">{item.secondary}</span>}
        </div>
      ))}
    </div>
  );
}

function useEntitySearch({ open, query, visibleModules }) {
  const enabled = open && query.length >= MIN_QUERY_LENGTH;

  // keepPreviousData: while a new query-keyed fetch is in flight, the previous
  // results stay visible instead of flashing the skeleton between keystrokes.
  const customers = useQuery({
    queryKey: ['palette', 'customers', query],
    queryFn: () => salesApi.listCustomers({ q: query, limit: GROUP_CAP }),
    enabled: enabled && visibleModules.has('sales'),
    gcTime: 60_000,
    placeholderData: keepPreviousData,
  });
  const products = useQuery({
    queryKey: ['palette', 'products', query],
    queryFn: () => inventoryApi.listProducts({ q: query, limit: GROUP_CAP }),
    enabled: enabled && visibleModules.has('inventory'),
    gcTime: 60_000,
    placeholderData: keepPreviousData,
  });
  // No q support on the finance invoice list → fetch the (bounded) list once
  // and filter client-side, so the key is intentionally query-independent.
  const invoices = useQuery({
    queryKey: ['palette', 'invoices'],
    queryFn: () => financeApi.listInvoices({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('finance'),
    staleTime: 30_000,
    gcTime: 60_000,
  });
  const projects = useQuery({
    queryKey: ['palette', 'projects'],
    queryFn: () => projectsApi.listProjects({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('projects'),
    staleTime: 30_000,
    gcTime: 60_000,
  });
  // Bounded fetch + client-side filter (no server q on these lists), same as
  // invoices/projects above. Query-independent keys so they cache once per open.
  const orders = useQuery({
    queryKey: ['palette', 'orders'],
    queryFn: () => salesApi.listOrders({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('sales'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const quotes = useQuery({
    queryKey: ['palette', 'quotes'],
    queryFn: () => salesApi.listQuotes({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('sales'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const tickets = useQuery({
    queryKey: ['palette', 'tickets'],
    queryFn: () => salesApi.listTickets(),
    enabled: enabled && visibleModules.has('sales'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const equipment = useQuery({
    queryKey: ['palette', 'equipment'],
    queryFn: () => financeApi.listEquipment(),
    enabled: enabled && visibleModules.has('finance'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const suppliers = useQuery({
    queryKey: ['palette', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers(),
    enabled: enabled && visibleModules.has('inventory'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const employees = useQuery({
    queryKey: ['palette', 'employees'],
    queryFn: () => hrApi.listEmployees({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('hr'),
    staleTime: 30_000, gcTime: 60_000,
  });
  const purchaseOrders = useQuery({
    queryKey: ['palette', 'purchaseOrders'],
    queryFn: () => inventoryApi.listPurchaseOrders({ limit: LIST_FETCH_LIMIT }),
    enabled: enabled && visibleModules.has('inventory'),
    staleTime: 30_000, gcTime: 60_000,
  });

  const queries = [customers, products, invoices, projects, orders, quotes, tickets, equipment, suppliers, employees, purchaseOrders];
  // Skeleton only for the very first fetch (no data, not even placeholder);
  // v5: isLoading === isPending && isFetching. Disabled queries never fetch.
  const isLoading = enabled && queries.some((q) => q.isLoading);
  // Used to gate the "no results" message while any refetch is still in flight.
  const isFetching = enabled && queries.some((q) => q.isFetching);

  return { customers, products, invoices, projects, orders, quotes, tickets, equipment, suppliers, employees, purchaseOrders, isLoading, isFetching };
}

function buildGroups({ t, sections, pageQuery, entityQuery, entities }) {
  const groups = [];

  // Pages are filtered on the un-debounced query: instant, no network.
  // Without a query the full page list shows (the panel scrolls); the cap only
  // applies while typing so entity groups keep room below the page matches.
  const pages = sections
    .flatMap((section) =>
      section.items.map((item) => ({
        ...item,
        label: t(item.labelKey ?? section.labelKey),
        /* Der BEREICH, in dem die Seite steht. Bisher stand hier der Pfad -
           "/hr/time" neben "/meine/zeit" neben "/projects/time". Wer "Zeit"
           sucht, bekommt drei Treffer und muss aus englischen Pfaden raten,
           welcher gemeint ist. Der Bereichsname beantwortet genau das. */
        bereich: t(section.labelKey),
        /* Und wo drei Seiten nah beieinander liegen, ein Halbsatz dazu.
           Nur dort, wo der Name allein nicht reicht. */
        hinweis: item.hintKey ? t(item.hintKey) : null,
      })),
    )
    .filter((page) => !pageQuery || matches(page.label, pageQuery) || matches(page.to, pageQuery))
    .slice(0, pageQuery ? PAGE_CAP : Infinity)
    .map((page) => ({
      kind: 'page',
      id: page.to,
      label: page.label,
      secondary: [page.bereich, page.hinweis].filter(Boolean).join(' · '),
      to: page.to,
    }));
  if (pages.length > 0) {
    groups.push({ key: 'pages', label: t('palette.pages'), items: pages });
  }

  const query = entityQuery;
  if (query.length >= MIN_QUERY_LENGTH) {
    const customers = (entities.customers.data?.items ?? []).slice(0, GROUP_CAP).map((c) => ({
      kind: 'customer', id: c.id, label: c.name,
      secondary: [c.code, c.email].filter(Boolean).join(' · '),
      to: `/sales/customers/${c.id}`,
    }));
    const products = (entities.products.data?.items ?? []).slice(0, GROUP_CAP).map((p) => ({
      kind: 'product', id: p.id, label: p.name, secondary: p.sku, to: '/inventory/products',
    }));
    const invoices = (entities.invoices.data?.items ?? [])
      .filter((inv) => matches(inv.invoiceNumber, query))
      .slice(0, GROUP_CAP)
      .map((inv) => ({
        kind: 'invoice', id: inv.id, label: inv.invoiceNumber,
        secondary: [formatCurrency(inv.totalAmount), inv.status].filter(Boolean).join(' · '),
        to: `/finance/invoices/${inv.id}`,
      }));
    const projects = (entities.projects.data?.items ?? [])
      .filter((p) => matches(p.code, query) || matches(p.name, query))
      .slice(0, GROUP_CAP)
      .map((p) => ({ kind: 'project', id: p.id, label: p.name, secondary: p.code, to: `/projects/${p.id}` }));
    const orders = (entities.orders.data?.items ?? [])
      .filter((o) => matches(o.orderNumber, query))
      .slice(0, GROUP_CAP)
      .map((o) => ({ kind: 'order', id: o.id, label: o.orderNumber, secondary: [formatCurrency(o.totalAmount, o.currency), o.status].filter(Boolean).join(' · '), to: `/sales/orders/${o.id}` }));
    const quotes = (entities.quotes.data?.items ?? [])
      .filter((q) => matches(q.quoteNumber, query))
      .slice(0, GROUP_CAP)
      .map((q) => ({ kind: 'quote', id: q.id, label: q.quoteNumber, secondary: [formatCurrency(q.totalAmount, q.currency), q.status].filter(Boolean).join(' · '), to: `/sales/quotes/${q.id}` }));
    const tickets = (entities.tickets.data?.items ?? [])
      .filter((tk) => matches(tk.ticketNumber, query) || matches(tk.subject, query))
      .slice(0, GROUP_CAP)
      .map((tk) => ({ kind: 'ticket', id: tk.id, label: `${tk.ticketNumber} · ${tk.subject}`, secondary: [tk.customerName, tk.status].filter(Boolean).join(' · '), to: '/sales/tickets' }));
    const equipment = (entities.equipment.data?.items ?? [])
      .filter((e) => matches(e.equipmentNumber, query) || matches(e.name, query))
      .slice(0, GROUP_CAP)
      .map((e) => ({ kind: 'equipment', id: e.id, label: `${e.equipmentNumber} · ${e.name}`, secondary: [e.location, e.status].filter(Boolean).join(' · '), to: '/finance/maintenance' }));
    const suppliers = (entities.suppliers.data?.items ?? [])
      .filter((s) => matches(s.name, query) || matches(s.code, query))
      .slice(0, GROUP_CAP)
      .map((s) => ({ kind: 'supplier', id: s.id, label: s.name, secondary: [s.code, s.email].filter(Boolean).join(' · '), to: '/inventory/suppliers' }));
    const employees = (entities.employees.data?.items ?? [])
      .filter((e) => matches(`${e.firstName} ${e.lastName}`, query) || matches(e.email, query))
      .slice(0, GROUP_CAP)
      .map((e) => ({
        kind: 'employee', id: e.id, label: `${e.firstName} ${e.lastName}`,
        secondary: [e.position, e.email].filter(Boolean).join(' · '),
        to: '/hr/employees',
      }));
    const purchaseOrders = (entities.purchaseOrders.data?.items ?? [])
      .filter((po) => matches(po.poNumber, query) || matches(po.supplierName, query))
      .slice(0, GROUP_CAP)
      .map((po) => ({
        kind: 'purchaseOrder', id: po.id, label: po.poNumber,
        secondary: [po.supplierName, po.status].filter(Boolean).join(' · '),
        to: `/inventory/purchase-orders/${po.id}`,
      }));

    if (customers.length) groups.push({ key: 'customers', label: t('palette.customers'), items: customers });
    if (products.length) groups.push({ key: 'products', label: t('palette.products'), items: products });
    if (invoices.length) groups.push({ key: 'invoices', label: t('palette.invoices'), items: invoices });
    if (orders.length) groups.push({ key: 'orders', label: t('palette.orders'), items: orders });
    if (quotes.length) groups.push({ key: 'quotes', label: t('palette.quotes'), items: quotes });
    if (tickets.length) groups.push({ key: 'tickets', label: t('palette.tickets'), items: tickets });
    if (equipment.length) groups.push({ key: 'equipment', label: t('palette.equipment'), items: equipment });
    if (suppliers.length) groups.push({ key: 'suppliers', label: t('palette.suppliers'), items: suppliers });
    if (employees.length) groups.push({ key: 'employees', label: t('palette.employees'), items: employees });
    if (purchaseOrders.length) groups.push({ key: 'purchaseOrders', label: t('palette.purchaseOrders'), items: purchaseOrders });
    if (projects.length) groups.push({ key: 'projects', label: t('palette.projects'), items: projects });
  }

  // Assign flat indices across groups for single-cursor keyboard navigation.
  let flatIndex = 0;
  const flat = [];
  for (const group of groups) {
    group.items = group.items.map((item) => {
      const indexed = { ...item, flatIndex };
      flat.push(indexed);
      flatIndex += 1;
      return indexed;
    });
  }
  return { groups, flat };
}

export function CommandPalette({ sections, t }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const trimmed = query.trim().toLowerCase();
  const debouncedQuery = useDebouncedValue(trimmed, DEBOUNCE_MS);

  const visibleModules = useMemo(
    () => new Set(sections.map((s) => s.module).filter(Boolean)),
    [sections],
  );

  // Register the module-level opener for the topbar trigger.
  useEffect(() => {
    openHandler = () => setOpen(true);
    return () => {
      openHandler = null;
    };
  }, []);

  // Global Ctrl+K / Cmd+K toggle. preventDefault stops the browser's own search.
  // Registered in the CAPTURE phase so no in-page handler can swallow it first.
  // Note: when the browser's own UI has focus (address bar right after a reload)
  // or an extension claims Ctrl+K at browser level, the page never receives the
  // key - that is why '/' is offered as a fallback below.
  useEffect(() => {
    function isTypingTarget(el) {
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    }
    function onKeyDown(event) {
      if (event.repeat || event.isComposing) {
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((value) => !value);
        return;
      }
      // '/' fallback (GitHub-style): browsers never reserve it, so it works even
      // where Ctrl+K is taken - but only while not typing into a field.
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target)) {
        event.preventDefault();
        setOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  // While open: focus the input, and restore focus to the previously focused
  // element (e.g. the topbar trigger) when the palette closes.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const previouslyFocused = document.activeElement;
    inputRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  // Document-level Escape while open: closes even if focus left the input.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function onDocumentKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        setQuery('');
        setSelectedIndex(0);
      }
    }
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => document.removeEventListener('keydown', onDocumentKeyDown);
  }, [open]);

  const entities = useEntitySearch({ open, query: debouncedQuery, visibleModules });

  const { groups, flat } = useMemo(
    () => buildGroups({ t, sections, pageQuery: trimmed, entityQuery: debouncedQuery, entities }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- entity results are covered by their data refs
    [t, sections, trimmed, debouncedQuery, entities.customers.data, entities.products.data, entities.invoices.data, entities.projects.data, entities.orders.data, entities.quotes.data, entities.tickets.data, entities.equipment.data, entities.suppliers.data, entities.employees.data, entities.purchaseOrders.data],
  );

  // Clamp during render: when the result set shrinks (debounce settles, fetch
  // resolves) the cursor lands on the last real row instead of pointing at a
  // row that no longer exists. Reset-to-0 happens only on query change.
  const safeIndex = Math.min(selectedIndex, Math.max(flat.length - 1, 0));

  function close() {
    setOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }

  function activate(item) {
    if (!item) {
      return;
    }
    navigate(item.to);
    close();
  }

  function onPanelKeyDown(event) {
    if (event.key === 'Tab') {
      // The input is the only focusable element inside the panel
      // (option rows are role="option" divs) → trap focus on it.
      event.preventDefault();
      inputRef.current?.focus();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex(flat.length ? (safeIndex + 1) % flat.length : 0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex(flat.length ? (safeIndex - 1 + flat.length) % flat.length : 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      activate(flat[safeIndex]);
    }
  }

  if (!open) {
    return null;
  }

  const showHint = trimmed.length < MIN_QUERY_LENGTH;
  // Only declare "no results" once the debounce has settled and fetches are done.
  const showEmpty =
    !showHint && !entities.isFetching && flat.length === 0 && debouncedQuery === trimmed;
  // First fetch only - refetches keep previous results via placeholderData.
  const showSkeleton = entities.isLoading && flat.length === 0;

  return (
    <div className="palette-backdrop" onClick={close}>
      <div
        className="palette-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.placeholder')}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onPanelKeyDown}
      >
        <div className="palette-input-row">
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            className="palette-input"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            aria-autocomplete="list"
            aria-activedescendant={flat.length ? `palette-opt-${safeIndex}` : undefined}
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedIndex(0);
            }}
          />
          <kbd className="palette-kbd">esc</kbd>
        </div>
        <div className="palette-results" id="palette-results" role="listbox">
          {groups.map((group) => (
            <PaletteGroup
              key={group.key}
              group={group}
              selectedIndex={safeIndex}
              onHover={setSelectedIndex}
              onActivate={activate}
            />
          ))}
        </div>
        <div role="status" className="palette-status">
          {showSkeleton && <PaletteSkeleton />}
          {showEmpty && <div className="palette-empty">{t('palette.noResults')}</div>}
          {showHint && <div className="palette-hint">{t('palette.hint')}</div>}
        </div>
      </div>
    </div>
  );
}
